import React, { useState, useEffect, useRef } from 'react'
import { Search, ChevronDown, User as UserIcon } from 'lucide-react'
import { userService, User } from '../../services/userService'
import { FILL_GRADIENT } from '../../constants/gradients'

interface UserSearchDropdownProps {
  onSelect: (user: User) => void
  placeholder?: string
  disabled?: boolean
  className?: string
}

const UserSearchDropdown: React.FC<UserSearchDropdownProps> = ({
  onSelect,
  placeholder = 'Search for users...',
  disabled = false,
  className = ''
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [users, setUsers] = useState<User[]>([])
  const [selectedUser, setSelectedUser] = useState<User | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const searchTimeoutRef = useRef<NodeJS.Timeout>()

  // Search users with debouncing
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current)
    }

    if (searchQuery.length >= 2) {
      searchTimeoutRef.current = setTimeout(async () => {
        setIsLoading(true)
        try {
          const results = await userService.searchUsers(searchQuery)
          setUsers(results)
        } catch (error) {
          console.error('User search error:', error)
          setUsers([])
        } finally {
          setIsLoading(false)
        }
      }, 300)
    } else {
      setUsers([])
    }

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current)
      }
    }
  }, [searchQuery])

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleUserSelect = (user: User) => {
    setSelectedUser(user)
    setSearchQuery('')
    setIsOpen(false)
    setUsers([])
    onSelect(user)
  }

  const handleInputFocus = () => {
    setIsOpen(true)
    if (selectedUser) {
      setSearchQuery(selectedUser.username)
      setSelectedUser(null)
    }
  }

  const getUserAvatar = (user: User) => {
    if (user.image_url) {
      return (
        <img 
          src={user.image_url} 
          alt={user.username}
          className="w-8 h-8 rounded-full object-cover"
        />
      )
    }
    
    return (
      <div 
        className="w-8 h-8 rounded-full flex items-center justify-center"
        style={{ background: FILL_GRADIENT }}
      >
        <span className="text-white font-semibold text-sm">
          {user.username.charAt(0).toUpperCase()}
        </span>
      </div>
    )
  }

  const displayValue = selectedUser ? selectedUser.username : searchQuery

  return (
    <div ref={dropdownRef} className={`relative ${className}`}>
      <div className="relative">
        <div className="relative">
          <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
          <input
            type="text"
            className={`
              w-full pl-11 pr-10 py-2.5 border-2 border-gray-200 dark:border-dark-border rounded-xl 
              transition-colors focus:outline-none focus:border-gray-300 dark:focus:border-gray-600 
              dark:bg-dark-bg dark:text-dark-text font-medium
              ${disabled ? 'bg-gray-100 opacity-50' : ''}
            `}
            placeholder={placeholder}
            value={displayValue}
            onChange={(e) => setSearchQuery(e.target.value)}
            onFocus={handleInputFocus}
            disabled={disabled}
          />
          <ChevronDown className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
        </div>

        {selectedUser && (
          <div className="absolute left-11 top-1/2 transform -translate-y-1/2 flex items-center gap-2 pointer-events-none">
            {getUserAvatar(selectedUser)}
            <span className="text-gray-900 dark:text-dark-text font-semibold">
              {selectedUser.username}
            </span>
          </div>
        )}
      </div>

      {isOpen && (
        <div className="absolute z-50 w-full mt-2 bg-white dark:bg-dark-surface border border-gray-200 dark:border-dark-border rounded-xl shadow-lg max-h-60 overflow-y-auto">
          {isLoading ? (
            <div className="px-4 py-4 text-center text-gray-500 dark:text-dark-text-muted">
              <div className="flex items-center justify-center gap-2">
                <div className="animate-spin rounded-full h-5 w-5 border-2 border-transparent border-t-pink-600"></div>
                <span className="font-medium">Searching...</span>
              </div>
            </div>
          ) : searchQuery.length < 2 ? (
            <div className="px-4 py-4 text-center text-gray-500 dark:text-dark-text-muted font-medium">
              Type at least 2 characters to search
            </div>
          ) : users.length === 0 ? (
            <div className="px-4 py-6 text-center text-gray-500 dark:text-dark-text-muted">
              <UserIcon className="w-10 h-10 mx-auto mb-2 text-gray-400" />
              <p className="font-medium">No users found for "{searchQuery}"</p>
            </div>
          ) : (
            <div className="py-2">
              {users.map((user) => (
                <button
                  key={user.id}
                  onClick={() => handleUserSelect(user)}
                  className="w-full px-4 py-3 text-left hover:bg-gray-50 dark:hover:bg-gray-800 flex items-center gap-3 transition-colors"
                >
                  {getUserAvatar(user)}
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-gray-900 dark:text-dark-text">
                      {user.username}
                    </div>
                    {user.display_name && user.display_name !== user.username && (
                      <div className="text-sm text-gray-500 dark:text-dark-text-muted truncate">
                        {user.display_name}
                      </div>
                    )}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default UserSearchDropdown 